import { useCallback, useState, type ReactNode } from 'react';
import styles from './FJWarningModal.module.less';
import classNames from 'classnames';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJAutoLoading from '../../../common/components/FJLoading/FJAutoLoading';
import type { FJWarningModalFooterBtn } from '../../../redux/slice/FJModalsSlice';

interface FJWarningModalProps {
    title: string;
    content: ReactNode;
    footerDates?: FJWarningModalFooterBtn[];
    onClose?: () => void;
}

const FJWarningModal = ({ title, content, footerDates, onClose }: FJWarningModalProps) => {
    const [pendingBtnIndex, setPendingBtnIndex] = useState<number | null>(null);
    const isBusy = pendingBtnIndex !== null;

    const handleClose = useCallback(() => {
        if (isBusy) {
            return;
        }
        onClose?.();
    }, [isBusy, onClose]);

    const handleBtnClick = useCallback(
        async (item: FJWarningModalFooterBtn, idx: number) => {
            if (isBusy) {
                return;
            }
            if (!item.btnCallback) {
                handleClose();
                return;
            }
            setPendingBtnIndex(idx);
            try {
                await item.btnCallback();
            } catch {
                // 接口失败：保持弹窗，由 finally 结束 loading
            } finally {
                setPendingBtnIndex(null);
            }
        },
        [isBusy, handleClose],
    );

    const _getFootBtn = () => {
        return (
            footerDates &&
            footerDates.map((item, idx) => {
                const isLoading = pendingBtnIndex === idx;
                const isDisabled = isBusy && !isLoading;
                return (
                    <div
                        key={idx}
                        className={classNames(styles.btn_box, item.btnType ? styles[item.btnType] : false, {
                            [styles.btn_box_disabled]: isDisabled,
                            [styles.btn_box_loading]: isLoading,
                        })}
                        onClick={() => {
                            void handleBtnClick(item, idx);
                        }}
                    >
                        <span className={styles.btn_text}>{item.btnText}</span>
                        {isLoading ? (
                            <FJAutoLoading
                                className={styles.btn_loading}
                                shown
                                strokeColor={item.btnType === 'warning' ? '#fff' : '#686c7e'}
                            />
                        ) : null}
                    </div>
                );
            })
        );
    };

    return (
        <div className={classNames(styles.box)}>
            <FJModalClose onClick={isBusy ? undefined : handleClose} />
            <div className={styles.title}>{title}</div>
            <div className={styles.content}>{content}</div>

            <div className={styles.footer}>{_getFootBtn()}</div>
        </div>
    );
};

export default FJWarningModal;
